import classNames from 'classnames';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';

interface VerticalSlideInOutProps {
  children: ReactNode;
  direction: 'forward' | 'backward' | 'manual';
  maxStepAnimationDuration?: number;
}

const MAX_STEP_ANIMATION_DURATION = 2500;
const CURRENT_STEP_FADE_OUT_DURATION = 300;

export const VerticalSlideInOut: FC<VerticalSlideInOutProps> = ({
  children,
  direction,
  maxStepAnimationDuration = MAX_STEP_ANIMATION_DURATION,
}) => {
  const [slotA, setSlotA] = useState<ReactNode>(children);
  const [slotB, setSlotB] = useState<ReactNode>(null);
  const [activeSlot, setActiveSlot] = useState<'A' | 'B'>('A');
  const [isAnimating, setIsAnimating] = useState(false);
  const currentChildrenRef = useRef<ReactNode>(children);

  if (children !== currentChildrenRef.current && !isAnimating) {
    currentChildrenRef.current = children;
    if (activeSlot === 'A') {
      setSlotB(children);
      setActiveSlot('B');
    } else {
      setSlotA(children);
      setActiveSlot('A');
    }
    setIsAnimating(true);
  }

  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        if (activeSlot === 'A') {
          setSlotB(null);
        } else {
          setSlotA(null);
        }
        setIsAnimating(false);
      }, maxStepAnimationDuration);

      return () => clearTimeout(timer);
    }
  }, [isAnimating, maxStepAnimationDuration, activeSlot]);

  const slotAIsActive = activeSlot === 'A';
  const slotAIsExiting = isAnimating && !slotAIsActive;
  const slotAIsEntering = isAnimating && slotAIsActive;

  const slotBIsActive = activeSlot === 'B';
  const slotBIsExiting = isAnimating && !slotBIsActive;
  const slotBIsEntering = isAnimating && slotBIsActive;

  return (
    <div className={classNames('relative w-full flex-grow overflow-scroll no-scrollbar')}>
      {slotA !== null && (
        <div
          className={classNames('absolute w-full h-full', {
            [`motion-opacity-out-[0%] motion-duration-[${CURRENT_STEP_FADE_OUT_DURATION}ms]`]: slotAIsExiting,
            'motion-translate-y-in-[80%] motion-opacity-in-[0%] motion-duration-[500ms] motion-ease-spring-bouncy':
              slotAIsEntering && (direction === 'forward' || direction === 'manual'),
            'motion-translate-y-in-[-80%] motion-opacity-in-[0%] motion-duration-[500ms] motion-ease-spring-bouncy':
              slotAIsEntering && direction === 'backward',
          })}
        >
          {slotA}
        </div>
      )}
      {slotB !== null && (
        <div
          className={classNames('absolute w-full h-full', {
            [`motion-opacity-out-[0%] motion-duration-[${CURRENT_STEP_FADE_OUT_DURATION}ms]`]: slotBIsExiting,
            'motion-translate-y-in-[80%] motion-opacity-in-[0%] motion-duration-[500ms] motion-ease-spring-bouncy':
              slotBIsEntering && (direction === 'forward' || direction === 'manual'),
            'motion-translate-y-in-[-80%] motion-opacity-in-[0%] motion-duration-[500ms] motion-ease-spring-bouncy':
              slotBIsEntering && direction === 'backward',
          })}
        >
          {slotB}
        </div>
      )}
    </div>
  );
};
